/**
 * 猫の描画。プロトタイプのドット感をそのまま持ってきている。
 * 状態は読むだけ。ここで cat の中身を書き換えたら §4.4 違反。
 */
import { TILE } from '../sim/grid';
import { CatState, Species } from '../sim/state';

export function drawCat(
  ctx: CanvasRenderingContext2D,
  cat: CatState,
  species: Species,
  selected: boolean,
): void {
  const coat = species.coat;
  const air = cat.mode === 'jump' || cat.mode === 'fall';
  const bob = cat.mode === 'walk' ? Math.sin(cat.t * 22) * 0.8 : 0;
  // 面食らっている間は首を振る（§5.1 演出）
  const look = cat.startled > 0 ? Math.sin(cat.startled * 18) * 2.2 : 0;

  // 状態は数値ではなく姿で見せる（§2.1）
  const sleeping = cat.busyKind === 'sleep' && cat.busy > 0;
  const eating = (cat.busyKind === 'eat' || cat.busyKind === 'drink') && cat.busy > 0;
  // 香箱を組んでいる／集会に混じっている：足をしまって低くなる
  const loafing = cat.busy > 0 && (cat.busyKind === 'loaf' || cat.busyKind === 'meet');
  // 壁の飾りに前足を伸ばしている：後ろ足で立って上を向く（§2.1 絵で見せる）
  const swatting = cat.busyKind === 'swat' && cat.busy > 0;
  // 用事があるのに届かない：耳を伏せて、身を縮めてじっとしている
  const sulking = cat.blocked > 2;
  // 前がつかえて待っている：足踏みして、少し身を寄せる
  const queued = cat.waiting > 0 && !eating;
  // 食事中は頭を下げる。不満な時は少し沈む
  const headDrop = eating ? 4.5 : sulking ? 1.5 : swatting ? -3.5 : 0;
  const crouch = sulking ? 1.5 : loafing ? 2 : swatting ? -3 : 0;
  const shuffle = queued ? Math.sin(cat.sit * 9) * 0.7 : 0;

  ctx.save();
  // 割れた窓から入ってくる途中（§3.6）
  if (cat.arriving > 0) {
    const u = cat.arriving / 1.4;
    ctx.globalAlpha = Math.max(0, 1 - u * 0.85);
    ctx.translate(cat.x + u * 16, cat.y);
  } else {
    ctx.translate(cat.x, cat.y);
  }
  ctx.scale(cat.face, 1);

  if (sleeping) {
    drawSleepingCat(ctx, cat, species, selected);
    ctx.restore();
    return;
  }

  if (selected) {
    ctx.strokeStyle = 'rgba(95,211,189,.9)';
    ctx.lineWidth = 1;
    ctx.strokeRect(-11, -22, 22, 23);
  }

  ctx.fillStyle = 'rgba(0,0,0,.3)';
  ctx.beginPath();
  ctx.ellipse(0, -1, 9, 2.5, 0, 0, Math.PI * 2);
  ctx.fill();

  ctx.fillStyle = coat.body;
  ctx.beginPath();
  ctx.ellipse(-1 + shuffle, -8 + bob + crouch, 9, 5.5 - crouch * 0.4, air ? -0.25 : 0, 0, Math.PI * 2);
  ctx.fill();

  ctx.fillStyle = coat.patch;
  ctx.beginPath();
  ctx.ellipse(-4 + shuffle, -9 + bob + crouch, 4, 3.5, 0, 0, Math.PI * 2);
  ctx.fill();

  ctx.strokeStyle = coat.body;
  ctx.lineWidth = 2.4;
  ctx.lineCap = 'round';
  ctx.beginPath();
  ctx.moveTo(-9 + shuffle, -9 + bob + crouch);
  if (sulking) {
    // 尻尾を体に巻きつけてじっとしている
    ctx.quadraticCurveTo(-15, -4, -6, -1);
  } else {
    ctx.quadraticCurveTo(-16, -12 - (air ? 6 : 0), -13, -3 - (air ? 8 : 0));
  }
  ctx.stroke();

  const hx = 8 + look + shuffle;
  const hy = -11 + bob + headDrop + crouch;
  ctx.fillStyle = coat.body;
  ctx.beginPath();
  ctx.arc(hx, hy, 5.2, 0, Math.PI * 2);
  ctx.fill();

  if (sulking) {
    // 耳を伏せる（§2.1「その猫が耳を伏せて、隅でじっとしている」）
    ctx.beginPath();
    ctx.moveTo(hx - 4.5, hy - 3);
    ctx.lineTo(hx - 6.5, hy - 0.5);
    ctx.lineTo(hx - 1.5, hy - 4.5);
    ctx.fill();
    ctx.beginPath();
    ctx.moveTo(hx + 1.5, hy - 4.5);
    ctx.lineTo(hx + 6, hy - 1.5);
    ctx.lineTo(hx + 4, hy - 4.5);
    ctx.fill();
  } else {
    ctx.beginPath();
    ctx.moveTo(hx - 3.5, hy - 4);
    ctx.lineTo(hx - 2.5, hy - 8.5);
    ctx.lineTo(hx + 0.5, hy - 4.5);
    ctx.fill();
    ctx.beginPath();
    ctx.moveTo(hx + 1.5, hy - 4.5);
    ctx.lineTo(hx + 3.5, hy - 8);
    ctx.lineTo(hx + 4.5, hy - 3.5);
    ctx.fill();
  }

  ctx.fillStyle = '#12161b';
  if (eating) {
    // 目を閉じて食べている
    ctx.fillRect(hx - 0.2, hy - 1.5, 2.2, 1);
    ctx.fillRect(hx + 2.8, hy - 1.5, 2.2, 1);
  } else {
    ctx.fillRect(hx, hy - 1.5, 1.6, 1.6);
    ctx.fillRect(hx + 3, hy - 1.5, 1.6, 1.6);
  }

  if (swatting) {
    // 前足を上へ。届くかどうかは猫の種類で決まる（§3.3）
    const swing = Math.sin(cat.sit * 9) * 2.4;
    ctx.strokeStyle = coat.body;
    ctx.lineWidth = 2.2;
    ctx.lineCap = 'round';
    ctx.beginPath();
    ctx.moveTo(5 + shuffle, -8 + bob + crouch);
    ctx.lineTo(10 + swing, -20 + bob + crouch);
    ctx.stroke();
  }

  drawMark(ctx, cat, coat, hx, hy, bob + crouch);

  if (!air) {
    ctx.strokeStyle = coat.body;
    ctx.lineWidth = 2;
    const sw =
      cat.mode === 'walk' ? Math.sin(cat.t * 22) * 2.5 : queued ? Math.sin(cat.sit * 14) * 1.2 : 0;
    ctx.beginPath();
    ctx.moveTo(4 + shuffle, -4 + bob + crouch);
    ctx.lineTo(4 + sw, -0.5);
    ctx.stroke();
    ctx.beginPath();
    ctx.moveTo(-5 + shuffle, -4 + bob + crouch);
    ctx.lineTo(-5 - sw, -0.5);
    ctx.stroke();
  }

  ctx.restore();
}

/**
 * 来歴のしるし（§1 裏テーマ）。
 * 説明はしない。台詞も出さない。ここにあるのは形だけで、
 * 意味はプレイヤーが勝手に気づく（気づかなくても成立する）。
 */
function drawMark(
  ctx: CanvasRenderingContext2D,
  cat: CatState,
  coat: { body: string; patch: string },
  hx: number,
  hy: number,
  yOff: number,
): void {
  switch (cat.mark) {
    case 'collar': {
      // 首のあたりだけ毛が薄い
      ctx.fillStyle = 'rgba(255,255,255,.14)';
      ctx.fillRect(hx - 5.5, hy + 3, 5, 2);
      break;
    }
    case 'earTip': {
      // 片耳の先が四角く欠けている
      ctx.fillStyle = '#12171e';
      ctx.fillRect(hx + 2.6, hy - 8.4, 2.4, 1.8);
      break;
    }
    case 'tailTip': {
      ctx.strokeStyle = coat.patch;
      ctx.lineWidth = 2.4;
      ctx.beginPath();
      ctx.moveTo(-13, -4 + yOff);
      ctx.lineTo(-13.5, -2 + yOff);
      ctx.stroke();
      break;
    }
    case 'limp': {
      // 前脚を少し浮かせている
      ctx.strokeStyle = coat.body;
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(5, -4 + yOff);
      ctx.lineTo(7, -2.5 + yOff);
      ctx.stroke();
      break;
    }
    default:
      break;
  }
}

/** 寝床で丸くなっている猫。目を閉じて、尻尾を巻いている */
function drawSleepingCat(
  ctx: CanvasRenderingContext2D,
  cat: CatState,
  species: Species,
  selected: boolean,
): void {
  const coat = species.coat;
  const breathe = Math.sin(cat.busy * 1.6) * 0.5;

  if (selected) {
    ctx.strokeStyle = 'rgba(95,211,189,.9)';
    ctx.lineWidth = 1;
    ctx.strokeRect(-11, -16, 22, 17);
  }

  ctx.fillStyle = 'rgba(0,0,0,.3)';
  ctx.beginPath();
  ctx.ellipse(0, -1, 10, 2.5, 0, 0, Math.PI * 2);
  ctx.fill();

  ctx.fillStyle = coat.body;
  ctx.beginPath();
  ctx.ellipse(0, -5 + breathe, 10, 4.6, 0, 0, Math.PI * 2);
  ctx.fill();
  ctx.fillStyle = coat.patch;
  ctx.beginPath();
  ctx.ellipse(-3, -6 + breathe, 4, 2.6, 0, 0, Math.PI * 2);
  ctx.fill();

  // 尻尾を体に巻きつける
  ctx.strokeStyle = coat.body;
  ctx.lineWidth = 2.2;
  ctx.lineCap = 'round';
  ctx.beginPath();
  ctx.moveTo(-9, -5 + breathe);
  ctx.quadraticCurveTo(-13, -1, -4, -1.5);
  ctx.stroke();

  const hx = 7;
  const hy = -6 + breathe;
  ctx.fillStyle = coat.body;
  ctx.beginPath();
  ctx.arc(hx, hy, 4.6, 0, Math.PI * 2);
  ctx.fill();
  // 耳
  ctx.beginPath();
  ctx.moveTo(hx - 3.5, hy - 3.2);
  ctx.lineTo(hx - 2.5, hy - 7);
  ctx.lineTo(hx + 0.5, hy - 3.8);
  ctx.fill();
  ctx.beginPath();
  ctx.moveTo(hx + 1.5, hy - 3.8);
  ctx.lineTo(hx + 3.5, hy - 6.6);
  ctx.lineTo(hx + 4.5, hy - 2.8);
  ctx.fill();
  // 閉じた目
  ctx.fillStyle = '#12161b';
  ctx.fillRect(hx - 0.4, hy - 0.6, 2.2, 0.9);
  ctx.fillRect(hx + 2.6, hy - 0.6, 2.2, 0.9);
}

/**
 * 窓の外で待っている、次に来たがっている猫（§3.6 / §3.3）。
 * この子が入って来られるかどうかが次の猫が来る条件なので、
 * 誰が待っているかは画面から読み取れないといけない（§2.1）。
 */
export function drawWaitingCat(
  ctx: CanvasRenderingContext2D,
  species: Species,
  x: number,
  y: number,
  t: number,
): void {
  const coat = species.coat;
  const sway = Math.sin(t * 0.9) * 0.6;
  ctx.save();
  ctx.globalAlpha = 0.55;
  ctx.translate(x, y);
  ctx.scale(-1, 1); // 建物のほうを向く

  ctx.fillStyle = coat.body;
  ctx.beginPath();
  ctx.ellipse(0, -5 + sway, 6.5, 6, 0, 0, Math.PI * 2);
  ctx.fill();
  ctx.strokeStyle = coat.body;
  ctx.lineWidth = 2;
  ctx.lineCap = 'round';
  ctx.beginPath();
  ctx.moveTo(-5, -3 + sway);
  ctx.quadraticCurveTo(-10, -1, -6, -8);
  ctx.stroke();

  ctx.fillStyle = coat.body;
  ctx.beginPath();
  ctx.arc(3, -12 + sway, 4.6, 0, Math.PI * 2);
  ctx.fill();
  ctx.beginPath();
  ctx.moveTo(-0.5, -15 + sway);
  ctx.lineTo(0.5, -19 + sway);
  ctx.lineTo(3, -15.5 + sway);
  ctx.fill();
  ctx.beginPath();
  ctx.moveTo(4, -15.5 + sway);
  ctx.lineTo(6, -18.5 + sway);
  ctx.lineTo(7, -15 + sway);
  ctx.fill();
  ctx.fillStyle = '#e8e2d6';
  ctx.fillRect(2.4, -12.8 + sway, 1.5, 1.5);
  ctx.fillRect(5, -12.8 + sway, 1.5, 1.5);
  ctx.restore();
}

/** 追いかけている猫の頭上に、小さな印を出す */
export function drawFollowMark(ctx: CanvasRenderingContext2D, cat: CatState): void {
  const y = cat.y - 26 + Math.sin(cat.sit * 3) * 1.2;
  ctx.fillStyle = 'rgba(95,211,189,.85)';
  ctx.beginPath();
  ctx.moveTo(cat.x, y + 4);
  ctx.lineTo(cat.x - 3.5, y - 1);
  ctx.lineTo(cat.x + 3.5, y - 1);
  ctx.closePath();
  ctx.fill();
}

/** 猫のクリック判定（タップで選択・追従） */
export function catHit(cat: CatState, x: number, y: number): boolean {
  return Math.abs(cat.x - x) < TILE * 0.5 && y > cat.y - TILE * 0.8 && y < cat.y + TILE * 0.2;
}
